import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import {
  completeSignUp,
  updateSubscriptionStatus,
} from 'containers/Auth/actions';
import { makeSelectProfile } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import SubscriptionOptions from 'components/SubscriptionOptions';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getUserFirstName } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '16px 32px',
    backgroundColor: 'white',
    width: 500,
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: '5%',
    left: '50%',
    marginLeft: -250,
    [theme.breakpoints.down('sm')]: {
      width: 300,
      left: '50%',
      marginLeft: -150,
    },
  },
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerText: {
    paddingTop: 6,
  },
  closeIcon: {
    padding: 6,
    marginRight: -6,
  },
  userContainer: {
    paddingTop: 24,
    paddingBottom: 30,
  },
  userWrapper: {
    border: '1px solid #E9ECED',
    borderRadius: 3,
    padding: '18px 24px',
  },
  userImage: {
    width: 63,
    height: 63,
    borderRadius: 2,
    objectFit: 'cover',
  },
  userInfoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  labelCheck: {
    color: theme.palette.text.primary,
    ...theme.typography.overline,
  },
  marginNone: {
    margin: '0 0 40px 0',
    alignItems: 'flex-start',
  },
  checkboxApply: {
    padding: '4px 14px 0 0',
  },
  checkboxIcon: {
    height: 17,
    width: 17,
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    marginTop: 8,
    marginBottom: 5,
    textTransform: 'uppercase',
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  textFieldContainer: {
    '& label': {
      fontSize: '1.063rem',
    },
    '& input': {
      fontSize: '1.063rem',
    },
  },
  form: {
    marginBottom: 12,
  },
}));

const SubscriptionForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const profile = useSelector(makeSelectProfile());
  const type = useRef(!profile.email ? 'email' : 'subscription');
  const clientDetails = useSelector(makeSelectClientDetails());
  const [accountSiteCopy, signInSiteCopy, signUpSiteCopy] = useSiteCopySelector(
    ['account', 'sign-in', 'sign-up'],
  );
  const [email, setEmail] = useState('');
  const [subscription, setSubscription] = useState(
    _get(accountSiteCopy, 'pageCopy.subscription', [])
      .filter(item => item.default)
      .map(item => item.value),
  );
  const classes = useStyles();

  let method;
  if (_get(profile, 'providerData.providerId', '').includes('microsoft.com'))
    method = 'Microsoft';
  else if (_get(profile, 'providerData.providerId', '').includes('google'))
    method = 'Google';
  else if (_get(profile, 'providerData.providerId', '').includes('facebook'))
    method = 'Facebook';
  else if (_get(profile, 'providerData.providerId', '').includes('saml'))
    method = _get(clientDetails, 'samlConfiguration.providerName');

  const firstName = getUserFirstName(profile);

  const onSubscriptionChange = value => {
    if (subscription.includes(value)) {
      setSubscription(subscription.filter(item => item !== value));
    } else {
      setSubscription(subscription.concat(value));
    }
  };

  const handleSubmit = () => {
    if (!profile.email) {
      dispatch(
        completeSignUp({
          data: {
            email,
            subscription,
          },
        }),
      );
    } else if (subscription.length > 0) {
      dispatch(
        updateSubscriptionStatus({
          email: profile.email,
          subscription,
        }),
      );
    } else {
      onClose();
    }
  };

  const content = () => {
    if (type.current === 'email') {
      return (
        <>
          <Grid item xs={12} className={classes.header}>
            <Typography
              variant="h5"
              component="span"
              color="primary"
              className={classes.headerText}
            >
              {_get(signUpSiteCopy, 'pageCopy.completeRegistrationHeader')}
            </Typography>
            {_get(signUpSiteCopy, 'pageCopy.completeRegistrationHelper') && (
              <Typography variant="body1" color="textPrimary" align="justify">
                {_get(signUpSiteCopy, 'pageCopy.completeRegistrationHelper')}
              </Typography>
            )}
            <IconButton
              className={classes.closeIcon}
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} className={classes.form}>
            <TextField
              name="email"
              classes={{
                root: classes.textFieldContainer,
              }}
              fullWidth
              label="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              required
              inputProps={{
                pattern: '^\\S+@\\S+\\.\\S+$',
              }}
            />
          </Grid>
        </>
      );
    }
    if (type.current === 'subscription') {
      return (
        <>
          <Grid item xs={12} className={classes.header}>
            <Typography
              variant="h5"
              component="span"
              color="primary"
              className={classes.headerText}
            >
              {_get(
                signInSiteCopy,
                'pageCopy.subscriptionStepHeader',
                '',
              ).replace('<method>', method)}
            </Typography>
            <IconButton
              className={classes.closeIcon}
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid item xs={12} className={classes.userContainer}>
            <div className={classes.userWrapper}>
              <Grid container spacing={2}>
                {profile.avatarUrl && (
                  <Grid item>
                    <img
                      src={profile.avatarUrl}
                      className={classes.userImage}
                      alt=""
                    />
                  </Grid>
                )}
                <Grid item xs className={classes.userInfoWrapper}>
                  <Typography color="primary" variant="h6">
                    {firstName}
                  </Typography>
                  <Typography color="textPrimary" variant="body2">
                    {profile.email}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </>
      );
    }
    return null;
  };

  return (
    <Grid container justifyContent="center" className={classes.container}>
      {content()}
      <Grid item xs={12}>
        <SubscriptionOptions
          subscription={subscription}
          onChange={onSubscriptionChange}
          isSmall
        />
      </Grid>
      <Grid item xs={12}>
        <ButtonBase className={classes.button} onClick={handleSubmit}>
          {_get(signInSiteCopy, 'pageCopy.subscriptionStepButton', '')}
        </ButtonBase>
      </Grid>
    </Grid>
  );
};

SubscriptionForm.propTypes = {
  onClose: PropTypes.func,
};

export default SubscriptionForm;
